/**
 * acceptanceSignerKind - Regras puras do aceite Titular/Testemunha (Core de Aceites)
 *
 * A escolha "Quem está assinando?" só existe no aceite do cliente (papel owner) e
 * somente quando o modelo de checklist habilita `allow_witness` (congelado na versão
 * publicada). Com a opção ligada, CPF e data de nascimento passam a ser sempre
 * exigidos — o titular confirma os próprios dados e a testemunha informa os dela.
 * O servidor revalida tudo em confirm_checklist_acceptance (v1.7.0).
 *
 * CHANGELOG:
 *   RELEASE: 2026-10-13 v1.0.0 - Versão inicial (extraída do AcceptanceDialog para testes).
 */

/** Exigências mínimas de evidência de um aceite (espelho de AcceptanceRequirements). */
export interface AcceptanceReqLike {
  signature_drawing: boolean;
  selfie: boolean;
  geolocation: boolean;
  document_cpf: boolean;
  birth_date?: boolean;
}

/** A escolha Titular/Testemunha só se aplica ao aceite do cliente com a opção ligada. */
export function witnessFlowEnabled(role: string, allowWitness: boolean | undefined): boolean {
  return role === 'owner' && !!allowWitness;
}

/**
 * Exigências efetivas da coleta: com o fluxo de testemunha ativo, CPF e nascimento
 * tornam-se obrigatórios independentemente do formato da política.
 */
export function effectiveAcceptanceRequirements(
  requirements: AcceptanceReqLike,
  role: string,
  allowWitness: boolean | undefined,
): AcceptanceReqLike {
  if (!witnessFlowEnabled(role, allowWitness)) return requirements;
  return { ...requirements, document_cpf: true, birth_date: true };
}

/** Marcador enviado ao servidor: só existe no fluxo de testemunha; demais casos = null. */
export function signerKindForSubmission(
  role: string,
  allowWitness: boolean | undefined,
  chosen: 'owner' | 'witness',
): 'owner' | 'witness' | null {
  return witnessFlowEnabled(role, allowWitness) ? chosen : null;
}

/** Titular usa exclusivamente o nome cadastrado; testemunha e equipe informam o próprio. */
export function acceptanceNameLocked(role: string, allowWitness: boolean | undefined, chosen: 'owner' | 'witness'): boolean {
  return role === 'owner' && !(witnessFlowEnabled(role, allowWitness) && chosen === 'witness');
}

/** Ignora o estado digitável quando o participante representa o cliente cadastrado. */
export function acceptanceSubmissionName(
  role: string, allowWitness: boolean | undefined, chosen: 'owner' | 'witness',
  registeredName: string | undefined, enteredName: string,
): string {
  return (acceptanceNameLocked(role, allowWitness, chosen) ? registeredName ?? '' : enteredName).trim();
}
